import { memo, useMemo, type MouseEvent } from 'react';
import { columnRects, type ComposeOutput, type ComposedLine, type GroupItem, type ID, type ImageItem, type Line, type PageItem, type RectItem, type Spread, type TextFrameItem, type Thread } from '@proofroom/schema';
import { frameGeometry } from '../model/frameGeometry';
import type { SnapshotIndex } from '../model/snapshotIndex';
import type { OversetInfo } from '../model/overset';
import { composedRangeExtent, composedRunProps } from './composed';
import { lineProps, type LineProps, type Measure } from './lines';
import { visibleItems, type LayerOverrides } from './layers';
import { opacityCss, paintCss, PLACEHOLDER_FILL, PLACEHOLDER_STROKE } from './tint';
import { clientToSpread } from './scale';
import type { TextRange } from '../state/viewerStore';

export interface SpreadSvgProps {
  idx: SnapshotIndex;
  spread: Spread;
  scale: number;
  layerOverrides: LayerOverrides;
  composed: Record<ID, ComposeOutput>;
  overset: Record<ID, OversetInfo>;
  selectedStoryId?: ID;
  caretParaId?: ID;
  selection: TextRange[];
  threads: Thread[];
  activeThreadId?: ID;
  showReference: boolean;
  referenceOpacity: number;
  showFrames: boolean;
  pinTool: boolean;
  textReady: boolean;
  fontLocks: Record<ID, ID[]>;
  measure?: Measure;
  onFrameClick(frame: TextFrameItem): void;
  onPin(point: { x: number; y: number }): void;
  onMarkerClick(threadId: ID): void;
}

const ACCENT = '#ea616a';
const DANGER = '#d1302c';

export const SpreadSvg = memo(function SpreadSvg(p: SpreadSvgProps) {
  const { spread, idx, scale } = p;
  const items = useMemo(() => visibleItems(spread.items, idx.layers, p.layerOverrides), [spread, idx, p.layerOverrides]);
  const art = spread.rasters?.art ? idx.assets[spread.rasters.art.assetId] : undefined;
  const full = spread.rasters?.full ? idx.assets[spread.rasters.full.assetId] : undefined;
  const w = spread.bounds.w, h = spread.bounds.h;

  // while text is not ready, the full raster (with text baked in) is what covers the spread; if the
  // export produced no full raster, painting nothing at all is worse than the art raster (text hidden)
  const artImage = art && spread.rasters?.art ? { asset: art, raster: spread.rasters.art } : undefined;
  const fullImage = full && spread.rasters?.full ? { asset: full, raster: spread.rasters.full } : undefined;
  const spreadImage = p.textReady ? artImage : (fullImage ?? artImage);

  const onSvgClick = (e: MouseEvent<SVGSVGElement>) => {
    if (!p.pinTool) return;
    const r = e.currentTarget.getBoundingClientRect();
    p.onPin(clientToSpread(e.clientX, e.clientY, r, scale));
  };

  return (
    <svg
      className={`canvas-svg${p.pinTool ? ' pin' : ''}`}
      viewBox={`0 0 ${w} ${h}`}
      width={w * scale}
      height={h * scale}
      xmlns="http://www.w3.org/2000/svg"
      onClick={onSvgClick}
      data-spread={spread.id}
    >
      <defs>
        <pattern id="missing-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="6" height="6" fill="#eeeeee" /><line x1="0" y1="0" x2="0" y2="6" stroke="#cfcfcf" strokeWidth="1.5" />
        </pattern>
      </defs>
      {spread.pages.map((pg) => <rect key={pg.id} x={pg.bounds.x} y={pg.bounds.y} width={pg.bounds.w} height={pg.bounds.h} fill="#ffffff" />)}
      {spreadImage && (
        <image href={spreadImage.asset.url} x={spreadImage.raster.bounds.x} y={spreadImage.raster.bounds.y} width={spreadImage.raster.bounds.w} height={spreadImage.raster.bounds.h} preserveAspectRatio="none" />
      )}
      {items.map((it) => <ItemNode key={it.id} item={it} ctx={p} />)}
      {/* pins sit above the raster and composed text, below the selection and caret chrome that Highlights draws */}
      <Markers ctx={p} />
      <Highlights ctx={p} />
      {p.showReference && full && spread.rasters?.full && (
        <image href={full.url} x={spread.rasters.full.bounds.x} y={spread.rasters.full.bounds.y} width={spread.rasters.full.bounds.w} height={spread.rasters.full.bounds.h} preserveAspectRatio="none" opacity={p.referenceOpacity} style={{ pointerEvents: 'none' }} />
      )}
    </svg>
  );
});

/**
 * With an art raster under the spread, everything except text is already painted; only items the
 * viewer has switched on (a creative option hidden at export) need vector drawing. An item is in
 * the raster when it was effectively visible at export time, which is its own `visible` flag and
 * its layer's, before any viewer override. Items switched off cannot be removed from the raster,
 * which is a known limitation until per-option rasters exist.
 */
function inRaster(item: PageItem, ctx: SpreadSvgProps): boolean {
  if (!ctx.spread.rasters?.art || !ctx.idx.assets[ctx.spread.rasters.art.assetId]) return false;
  return item.visible !== false && (ctx.idx.layers[item.layerId]?.visible ?? true);
}

function ItemNode({ item, ctx }: { item: PageItem; ctx: SpreadSvgProps }) {
  if (item.kind !== 'text' && item.kind !== 'group' && inRaster(item, ctx)) return null;
  switch (item.kind) {
    case 'text': return <TextFrameNode frame={item} ctx={ctx} />;
    case 'image': return <ImageNode item={item} ctx={ctx} />;
    case 'group': return <GroupNode item={item} ctx={ctx} />;
    default: return <ShapeNode item={item} ctx={ctx} />;
  }
}

function strokeProps(item: PageItem, swatches: SpreadSvgProps['idx']['snapshot']['swatches']) {
  const s = item.stroke;
  if (!s || !s.weight) return {};
  const c = paintCss(s, swatches);
  return c === 'none' ? {} : { stroke: c, strokeWidth: s.weight };
}

function ShapeNode({ item, ctx }: { item: RectItem; ctx: SpreadSvgProps }) {
  const b = item.bounds;
  const sw = ctx.idx.snapshot.swatches;
  const common = { fill: paintCss(item.fill, sw), opacity: opacityCss(item.opacity), ...strokeProps(item, sw) };
  if (item.kind === 'oval') return <ellipse cx={b.x + b.w / 2} cy={b.y + b.h / 2} rx={b.w / 2} ry={b.h / 2} {...common} />;
  if (item.kind === 'line') {
    const stroke = strokeProps(item, sw);
    return <line x1={b.x} y1={b.y} x2={b.x + b.w} y2={b.y + b.h} stroke={stroke.stroke ?? '#000'} strokeWidth={stroke.strokeWidth ?? 0.5} opacity={common.opacity} />;
  }
  return <rect x={b.x} y={b.y} width={b.w} height={b.h} rx={item.cornerRadius ?? 0} {...common} />;
}

function ImageNode({ item, ctx }: { item: ImageItem; ctx: SpreadSvgProps }) {
  const b = item.bounds;
  const g = item.graphicBounds ?? b;
  const asset = item.assetId ? ctx.idx.assets[item.assetId] : undefined;
  const clipId = `clip-${item.id}`;
  const sw = ctx.idx.snapshot.swatches;
  const frameFill = paintCss(item.fill, sw);
  return (
    <g opacity={opacityCss(item.opacity)}>
      <clipPath id={clipId}><rect x={b.x} y={b.y} width={b.w} height={b.h} rx={item.cornerRadius ?? 0} /></clipPath>
      {frameFill !== 'none' && <rect x={b.x} y={b.y} width={b.w} height={b.h} rx={item.cornerRadius ?? 0} fill={frameFill} />}
      {item.isPlaceholder ? (
        <g clipPath={`url(#${clipId})`}>
          <rect x={b.x} y={b.y} width={b.w} height={b.h} fill={PLACEHOLDER_FILL} />
          <line x1={b.x} y1={b.y} x2={b.x + b.w} y2={b.y + b.h} stroke={PLACEHOLDER_STROKE} strokeWidth={0.5} />
          <line x1={b.x + b.w} y1={b.y} x2={b.x} y2={b.y + b.h} stroke={PLACEHOLDER_STROKE} strokeWidth={0.5} />
          <text x={b.x + 4} y={b.y + 10} fontFamily="Inter, system-ui, sans-serif" fontSize={7} fill="#8a8a8a">Image placeholder</text>
        </g>
      ) : asset ? (
        <image href={asset.url} x={g.x} y={g.y} width={g.w} height={g.h} preserveAspectRatio="none" clipPath={`url(#${clipId})`} />
      ) : (
        <g clipPath={`url(#${clipId})`}>
          <rect x={b.x} y={b.y} width={b.w} height={b.h} fill="url(#missing-hatch)" />
          <text x={b.x + 4} y={b.y + 10} fontFamily="Inter, system-ui, sans-serif" fontSize={7} fill="#8a8a8a">{item.linkStatus === 'missing' ? 'Missing link' : 'Image not exported'}{item.linkPath ? `: ${item.linkPath.split('/').pop()}` : ''}</text>
        </g>
      )}
      <rect x={b.x} y={b.y} width={b.w} height={b.h} rx={item.cornerRadius ?? 0} fill="none" {...strokeProps(item, sw)} />
    </g>
  );
}

function GroupNode({ item, ctx }: { item: GroupItem; ctx: SpreadSvgProps }) {
  const children = visibleItems(item.children, ctx.idx.layers, ctx.layerOverrides);
  return <g opacity={opacityCss(item.opacity)}>{children.map((c) => <ItemNode key={c.id} item={c} ctx={ctx} />)}</g>;
}

function TextFrameNode({ frame, ctx }: { frame: TextFrameItem; ctx: SpreadSvgProps }) {
  const b = frame.bounds;
  const sw = ctx.idx.snapshot.swatches;
  const story = ctx.idx.stories[frame.storyId];
  const composed = ctx.composed[frame.storyId];
  const selected = ctx.selectedStoryId === frame.storyId;
  const over = ctx.overset[frame.storyId];
  const isOversetFrame = over?.overset && over.lastFrameId === frame.id;
  const fontLock = ctx.fontLocks[frame.storyId];
  const locked = !!(story?.hasTable || frame.hasTable || fontLock);
  const full = ctx.spread.rasters?.full ? ctx.idx.assets[ctx.spread.rasters.full.assetId] : undefined;
  const fill = paintCss(frame.fill, sw);
  const stroke = strokeProps(frame, sw);
  const raster = frame.raster ? ctx.idx.assets[frame.raster.assetId] : undefined;

  const lines = useMemo(() => {
    if (composed || !story || locked) return [];
    const lookup = { paraStyles: ctx.idx.paraStyles, charStyles: ctx.idx.charStyles, fonts: ctx.idx.fonts };
    const cols = columnRects(frameGeometry(frame));
    const pageNumber = frame.pageId ? ctx.idx.pages[frame.pageId]?.number ?? '' : '';
    const out: LineProps[] = [];
    const all = frame.lines ?? [];
    all.forEach((line, i) => {
      const para = ctx.idx.paragraphs[line.paraId]?.paragraph;
      if (!para) return;
      const next = all[i + 1];
      const lastInParagraph = line.end >= para.text.length || !next || next.paraId !== line.paraId;
      out.push(lineProps(line, para, lookup, ctx.measure, { columnX: cols[line.column]?.x ?? frame.bounds.x, pageNumber, lastInParagraph }));
    });
    return out;
  }, [composed, story, frame, locked, ctx.idx, ctx.measure]);

  const composedHere = useMemo(() => (composed ? composed.lines.filter((l) => l.frameId === frame.id) : []), [composed, frame.id]);

  return (
    <g data-frame={frame.id} data-story={frame.storyId} opacity={opacityCss(frame.opacity)}>
      {(fill !== 'none' || stroke.stroke) && <rect x={b.x} y={b.y} width={b.w} height={b.h} rx={frame.cornerRadius ?? 0} fill={fill} {...stroke} />}
      {ctx.textReady ? (
        fontLock && full && ctx.spread.rasters?.full ? (
          // the face is not in the browser, so show InDesign's own rendering of this frame instead of a substitute font
          <g clipPath={`url(#clip-${frame.id})`}>
            <clipPath id={`clip-${frame.id}`}><rect x={b.x} y={b.y} width={b.w} height={b.h} /></clipPath>
            <image href={full.url} x={ctx.spread.rasters.full.bounds.x} y={ctx.spread.rasters.full.bounds.y} width={ctx.spread.rasters.full.bounds.w} height={ctx.spread.rasters.full.bounds.h} preserveAspectRatio="none" />
          </g>
        ) : locked ? (
          // the art raster is exported with text frames hidden, so a table always needs its own raster
          raster && frame.raster
            ? <image href={raster.url} x={frame.raster.bounds.x} y={frame.raster.bounds.y} width={frame.raster.bounds.w} height={frame.raster.bounds.h} preserveAspectRatio="none" />
            // a font-locked frame with no raster to show is not a table: name the face rather than blaming the export
            : fontLock
              ? <FrameFallback frame={frame} scale={ctx.scale} label="Font not available" detail={fontLock.join(', ')} />
              : <FrameFallback frame={frame} scale={ctx.scale} label="Table not exported" />
        ) : composed ? (
          composedHere.map((l) => <ComposedLineNode key={`${l.paraId}:${l.start}:${l.column}`} line={l} />)
        ) : (
          lines.map((l) => <LineNode key={l.key} line={l} />)
        )
      ) : null}
      {(ctx.showFrames || selected) && (
        <rect x={b.x} y={b.y} width={b.w} height={b.h} fill="none" stroke={selected ? ACCENT : '#9aa4b8'} strokeWidth={selected ? 1.5 / ctx.scale : 0.75 / ctx.scale} strokeDasharray={selected ? undefined : `${3 / ctx.scale} ${3 / ctx.scale}`} pointerEvents="none" />
      )}
      {isOversetFrame && (
        <g pointerEvents="none">
          <rect x={b.x} y={b.y} width={b.w} height={b.h} fill="none" stroke={DANGER} strokeWidth={2 / ctx.scale} />
          <OversetBadge x={b.x + b.w} y={b.y + b.h} scale={ctx.scale} lines={over.lines} exact={over.exact} />
        </g>
      )}
      {!ctx.pinTool && (
        <rect
          className={`frame-hit${locked ? ' locked' : ''}`}
          x={b.x} y={b.y} width={b.w} height={b.h}
          onClick={(e) => { e.stopPropagation(); if (!locked) ctx.onFrameClick(frame); }}
        >
          <title>{fontLock ? `Font ${fontLock.join(', ')} is not available; ask the designer to publish with it installed` : locked ? 'Tables are not editable in this version' : `Edit story ${frame.storyId}`}</title>
        </rect>
      )}
    </g>
  );
}

/** A frame with nothing to show, because the exporter produced no raster for it: say so rather than leaving a hole in the page. */
function FrameFallback({ frame, scale, label, detail }: { frame: TextFrameItem; scale: number; label: string; detail?: string }) {
  const b = frame.bounds;
  const s = 1 / scale;
  const cy = b.y + b.h / 2;
  return (
    <g pointerEvents="none">
      <rect x={b.x} y={b.y} width={b.w} height={b.h} fill="url(#missing-hatch)" />
      <rect x={b.x} y={b.y} width={b.w} height={b.h} fill="none" stroke="#b8b2a8" strokeWidth={0.75 * s} strokeDasharray={`${3 * s} ${3 * s}`} />
      <text x={b.x + b.w / 2} y={detail ? cy - 7 * s : cy} textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize={11 * s} fill="#6b6b6b">{label}</text>
      {detail && <text x={b.x + b.w / 2} y={cy + 7 * s} textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize={9 * s} fill="#6b6b6b">{detail}</text>}
    </g>
  );
}

function OversetBadge({ x, y, scale, lines, exact }: { x: number; y: number; scale: number; lines: number; exact: boolean }) {
  const s = 1 / scale;
  const label = `${exact ? '' : 'at least '}${lines} line${lines === 1 ? '' : 's'} over`;
  const w = (label.length * 6.2 + 16) * s;
  const h = 18 * s;
  return (
    <g transform={`translate(${x - w}, ${y + 4 * s})`}>
      <rect width={w} height={h} rx={h / 2} fill={DANGER} />
      <text x={w / 2} y={h * 0.68} textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize={11 * s} fontWeight={600} fill="#fff">{label}</text>
    </g>
  );
}

function LineNode({ line }: { line: LineProps }) {
  const single = line.spans.length === 1 && line.spans[0].x === undefined;
  const first = line.spans[0];
  // the bullet is not in the paragraph's text, so it is its own text element, before the line's own
  const marker = line.marker && <text x={line.marker.x} y={line.y} fontFamily={line.marker.fontFamily} fontSize={line.marker.fontSize} fill={line.marker.fill} data-marker="bullet">{line.marker.text}</text>;
  if (!first) return marker ?? null;
  const common = {
    x: line.x,
    y: line.y,
    xmlSpace: 'preserve' as const,
    textAnchor: line.anchor === 'start' ? undefined : line.anchor,
    textLength: line.textLength,
    lengthAdjust: line.textLength !== undefined ? ('spacingAndGlyphs' as const) : undefined,
    'data-para': line.paraId,
    'data-start': line.start,
  };
  if (single) {
    return (
      <>
        {marker}
        <text {...common} fontFamily={first.fontFamily} fontSize={first.fontSize} fill={first.fill} letterSpacing={first.letterSpacing || undefined} dy={first.dy} fontVariant={first.fontVariant} textDecoration={first.underline ? 'underline' : first.strikethrough ? 'line-through' : undefined}>
          {first.text}
        </text>
      </>
    );
  }
  return (
    <>
      {marker}
      <text {...common} fontFamily={first.fontFamily} fontSize={first.fontSize} fill={first.fill}>
        {line.spans.map((s, i) => (
          <tspan key={i} x={s.x} fontFamily={s.fontFamily} fontSize={s.fontSize} fill={s.fill} letterSpacing={s.letterSpacing || undefined} dy={s.dy} fontVariant={s.fontVariant} textDecoration={s.underline ? 'underline' : s.strikethrough ? 'line-through' : undefined}>{s.text}</tspan>
        ))}
      </text>
    </>
  );
}

function ComposedLineNode({ line }: { line: ComposedLine }) {
  const runs = composedRunProps(line);
  return (
    <g data-para={line.paraId} data-start={line.start}>
      {runs.map((r) => (
        <text key={r.key} x={r.xs.join(' ')} y={r.y} fontFamily={r.fontFamily} fontSize={r.fontSize} fill={r.fill} fontVariant={r.fontVariant} xmlSpace="preserve">{r.text}</text>
      ))}
    </g>
  );
}

/** Lines of a story on this spread, whichever source is live (engine output or InDesign's lines). */
function storyLines(ctx: SpreadSvgProps, storyId: ID): Array<Line | ComposedLine> {
  const composed = ctx.composed[storyId];
  if (composed) return composed.lines.filter((l) => ctx.idx.itemSpread[l.frameId] === ctx.spread.index);
  const story = ctx.idx.stories[storyId];
  if (!story) return [];
  const out: Line[] = [];
  for (const fid of story.frameIds.length ? story.frameIds : Object.keys(ctx.idx.frames).filter((k) => ctx.idx.frames[k].storyId === storyId)) {
    const f = ctx.idx.frames[fid];
    if (!f || ctx.idx.itemSpread[fid] !== ctx.spread.index) continue;
    out.push(...(f.lines ?? []));
  }
  return out;
}

function rangeExtent(line: Line | ComposedLine, start: number, end: number): { x0: number; x1: number } | undefined {
  if ('runs' in line && line.runs?.length) return composedRangeExtent(line, start, end);
  const s = Math.max(start, line.start), e = Math.min(end, line.end);
  if (e <= s) return undefined;
  const n = Math.max(1, line.end - line.start);
  return { x0: line.x + ((s - line.start) / n) * line.width, x1: line.x + ((e - line.start) / n) * line.width };
}

function Highlights({ ctx }: { ctx: SpreadSvgProps }) {
  if (!ctx.selectedStoryId) return null;
  const lines = storyLines(ctx, ctx.selectedStoryId);
  const para = ctx.caretParaId ? lines.filter((l) => l.paraId === ctx.caretParaId) : [];
  const sel: Array<{ x: number; y: number; w: number; h: number }> = [];
  for (const r of ctx.selection) {
    if (r.end <= r.start) continue;
    for (const l of lines) {
      if (l.paraId !== r.paraId) continue;
      const ext = rangeExtent(l, r.start, r.end);
      if (ext) sel.push({ x: ext.x0, y: l.baseline - l.ascent, w: Math.max(0.5, ext.x1 - ext.x0), h: l.ascent + l.descent });
    }
  }
  return (
    <g pointerEvents="none">
      {para.map((l, i) => <rect key={i} x={l.x - 2} y={l.baseline - l.ascent - 1} width={Math.max(l.width, 4) + 4} height={l.ascent + l.descent + 2} fill={ACCENT} opacity={0.08} />)}
      {sel.map((r, i) => <rect key={`s${i}`} x={r.x} y={r.y} width={r.w} height={r.h} fill={ACCENT} opacity={0.28} />)}
    </g>
  );
}

function Markers({ ctx }: { ctx: SpreadSvgProps }) {
  const s = 1 / ctx.scale;
  const r = 8 * s;
  const marks: Array<{ id: ID; x: number; y: number; n: number; open: boolean }> = [];
  ctx.threads.forEach((t, i) => {
    const a = t.anchor;
    if (a.spreadId && a.point) {
      if (a.spreadId === ctx.spread.id) marks.push({ id: t.id, x: a.point.x, y: a.point.y, n: i + 1, open: t.status === 'open' });
      return;
    }
    if (a.storyId && a.paraId) {
      const lines = storyLines(ctx, a.storyId).filter((l) => l.paraId === a.paraId);
      const line = lines.find((l) => a.start === undefined || (l.end > a.start)) ?? lines[0];
      if (line) marks.push({ id: t.id, x: line.x - 10 * s, y: line.baseline - line.ascent / 2, n: i + 1, open: t.status === 'open' });
    }
  });
  return (
    <g>
      {marks.map((m) => {
        const active = m.id === ctx.activeThreadId;
        return (
          <g key={m.id} transform={`translate(${m.x}, ${m.y})`} style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); ctx.onMarkerClick(m.id); }}>
            <circle r={r} fill={m.open ? (active ? ACCENT : '#ffc400') : '#c9c9c9'} stroke={active ? '#fff' : 'rgba(0,0,0,0.25)'} strokeWidth={1.5 * s} />
            <text textAnchor="middle" y={3.5 * s} fontFamily="Inter, system-ui, sans-serif" fontSize={10 * s} fontWeight={700} fill={m.open && !active ? '#141414' : '#fff'}>{m.n}</text>
          </g>
        );
      })}
    </g>
  );
}
